<template>

  <div
      v-if="isProviderLinked && shouldRender"
      class="delivery-order-provider-sync">

    <!-- Quiet loading state -->
    <div v-if="isDataLoading" class="sync-checking">
      <v-progress-circular indeterminate :size="24" color="primary" />
      <span>{{ loadingText }}</span>
    </div>

    <!-- Provider comparison status and delivery guard -->
    <v-container
        v-else-if="dataLoadingError || isProviderOrderUnavailable || requiresSync"
        fluid
        class="sync-guard pa-5">
      <div class="sync-guard-content">
        <template v-if="isProviderOrderUnavailable">
          <v-icon class="sync-guard-icon" color="amber darken-2">mdi-alert-outline</v-icon>
          <div class="sync-guard-title">{{ providerName }} order is no longer available.</div>
        </template>

        <template v-else-if="dataLoadingError">
          <v-icon class="sync-guard-icon" color="amber darken-2">mdi-alert-outline</v-icon>
          <div class="sync-guard-title">{{ dataLoadingError }}</div>
          <v-btn
              outlined
              color="primary"
              class="sync-guard-action"
              @click="performCompare">
            Try Again
          </v-btn>
        </template>

        <template v-else>
          <v-icon class="sync-guard-icon" color="amber darken-2">mdi-alert-outline</v-icon>
          <div class="sync-guard-title">{{ providerName }} order info has changed!</div>
          <v-btn
              color="orange darken-3"
              class="sync-guard-action"
              @click="openReview">
            Review / Handle
          </v-btn>
        </template>
      </div>

      <div v-if="!isProviderOrderUnavailable" class="sync-guard-message">
        {{ guardMessage }}
      </div>
    </v-container>

    <!-- Review, sync, and result dialog -->
    <v-dialog
        v-if="compareResult"
        persistent
        :fullscreen="isMobileView"
        max-width="850"
        v-model="isDialogOpen">
      <v-card
          :class="[
            'sync-dialog',
            { 'sync-dialog--fullscreen': isMobileView }
          ]">

        <template v-if="syncSucceeded">
          <v-card-text class="sync-result-content">
            <div class="sync-success-heading">
              <v-icon class="sync-success-icon" color="green lighten-1">mdi-check</v-icon>
              <div>Order info has been synced.</div>
            </div>
            <div class="sync-reminder">
              <v-icon class="sync-reminder-icon" color="grey lighten-2">mdi-file-multiple-outline</v-icon>
              <div>Remember to upload any affected<br>supporting docs.</div>
            </div>
          </v-card-text>
          <v-card-actions class="justify-center pb-8">
            <v-btn outlined color="primary" @click="closeDialog">Close</v-btn>
          </v-card-actions>
        </template>

        <template v-else-if="syncError">
          <v-card-text class="sync-result-content">
            <v-icon class="sync-error-icon" color="red lighten-1">mdi-alert-circle-outline</v-icon>
            <div class="sync-error-heading">Order information could not be synced.</div>
            <div class="sync-error-message">{{ syncError }}</div>
          </v-card-text>
          <v-card-actions class="sync-error-actions justify-center pb-8">
            <v-btn text color="primary" :disabled="isOrderUpdating" @click="closeDialog">Close</v-btn>
            <v-btn
                color="orange darken-3"
                :disabled="isOrderUpdating"
                @click="retrySync">
              <v-progress-circular
                  v-if="isOrderUpdating"
                  indeterminate
                  :size="20"
                  color="white" />
              <span v-else>Try Again</span>
            </v-btn>
          </v-card-actions>
        </template>

        <template v-else>
          <v-card-title class="sync-dialog-header">
            <div class="sync-dialog-heading-row">
              <div class="sync-dialog-title">Current Changes from {{ providerName }}</div>
              <v-btn text color="primary" :disabled="isOrderUpdating" @click="closeDialog">
                Close
              </v-btn>
            </div>
            <div class="sync-dialog-subtitle">
              <span>Order: {{ compareResult.order_number }}</span>
              <span class="sync-dialog-status-separator">&mdash;</span>
              <span class="sync-dialog-status-label">provider status:</span>
              <span>{{ compareResult.thirdparty_status }}</span>
            </div>
          </v-card-title>
          <v-card-text class="sync-details-content">
            <order-provider-compare-results
                v-if="compareResult"
                :compareResult="compareResult"
                :hideHeader="true" />
          </v-card-text>
          <v-card-actions class="sync-dialog-actions">
            <v-btn text color="primary" :disabled="isOrderUpdating" @click="closeDialog">
              Close
            </v-btn>
            <v-btn
                color="orange darken-3"
                min-width="192"
                :disabled="isOrderUpdating"
                @click="syncProviderData">
              <v-progress-circular
                  v-if="isOrderUpdating"
                  indeterminate
                  :size="20"
                  color="white" />
              <span v-else>Sync Changes</span>
            </v-btn>
            <div class="sync-dialog-warning">{{ guardMessage }}</div>
          </v-card-actions>
        </template>

      </v-card>
    </v-dialog>

  </div>

</template>

<script>
import OrderProviderCompareResults from '@/components/orders/OrderProviderCompareResults'

export default {
  name: 'DeliveryOrderProviderSync',

  components: {
    OrderProviderCompareResults
  },

  props: {
    order: {
      type: Object,
      required: true
    },
    blockUntilSynced: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      isDataLoading: false,
      dataLoadingError: null,
      isProviderOrderMissing: false,
      isOrderUpdating: false,
      syncError: null,
      syncSucceeded: false,
      providerSyncApplied: false,
      isDialogOpen: false,
      compareResult: null
    }
  },

  computed: {
    isMobileView () {
      return this.$vuetify.breakpoint.smAndDown
    },
    isProviderLinked () {
      return Boolean(this.order.provider_alias && this.order.thirdparty_order_id)
    },
    providerName () {
      return this.order.provider_alias
        ? this.$root.providerAliasToName(this.order.provider_alias)
        : 'Provider'
    },
    loadingText () {
      return `Checking ${this.providerName} order info`
    },
    guardMessage () {
      return 'Order cannot be delivered until synced.'
    },
    diff () {
      return this.compareResult?.diff
    },
    isProviderOrderCanceled () {
      return Boolean(
        this.compareResult &&
        ['rejected', 'canceled', 'cancelled'].includes(this.compareResult.thirdparty_status)
      )
    },
    isProviderOrderUnavailable () {
      return this.isProviderOrderMissing || this.isProviderOrderCanceled
    },
    requiresSync () {
      return Boolean(this.compareResult && this.diff && !this.diff.same && !this.isProviderOrderCanceled)
    },
    isBlocked () {
      if (!this.blockUntilSynced || !this.isProviderLinked || this.syncSucceeded) return false
      if (this.isDataLoading || this.dataLoadingError || !this.compareResult) return true
      return this.isProviderOrderCanceled || !this.diff?.same
    },
    providerOrderKey () {
      return `${this.order.id}:${this.order.provider_alias}:${this.order.thirdparty_order_id}`
    },
    shouldRender () {
      return Boolean(
        this.isDataLoading ||
        this.dataLoadingError ||
        this.isProviderOrderUnavailable ||
        this.requiresSync ||
        this.isDialogOpen
      )
    }
  },

  watch: {
    isBlocked: {
      immediate: true,
      handler (isBlocked) {
        this.$emit('sync-state', isBlocked)
      }
    },
    providerOrderKey (updated, previous) {
      if (updated !== previous) this.resetAndCompare()
    }
  },

  mounted () {
    if (this.isProviderLinked) this.performCompare()
  },

  methods: {
    normalizeError (err, fallback) {
      if (!err) return fallback
      if (typeof err === 'string') return err
      return err.message || err.text || fallback
    },

    resetAndCompare () {
      this.dataLoadingError = null
      this.isProviderOrderMissing = false
      this.syncError = null
      this.syncSucceeded = false
      this.providerSyncApplied = false
      this.compareResult = null
      this.isDialogOpen = false
      if (this.isProviderLinked) this.performCompare()
    },

    async performCompare () {
      if (!this.isProviderLinked || this.isDataLoading) return

      this.isDataLoading = true
      this.dataLoadingError = null
      this.isProviderOrderMissing = false

      try {
        this.compareResult = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${this.order.id}/provider-compare`
        })
      } catch (err) {
        this.compareResult = null
        const normalizedError = this.normalizeError(err, '')
        this.isProviderOrderMissing = err?.status === 404 || normalizedError === 'Not Found'
        if (!this.isProviderOrderMissing) {
          this.dataLoadingError = normalizedError || `${this.providerName} order info could not be checked.`
        }
      } finally {
        this.isDataLoading = false
      }
    },

    openReview () {
      this.syncError = null
      this.isDialogOpen = true
    },

    closeDialog () {
      if (!this.isOrderUpdating) this.isDialogOpen = false
    },

    async syncProviderData () {
      if (this.isOrderUpdating || !this.requiresSync) return

      this.isOrderUpdating = true
      this.syncError = null

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `order/${this.order.id}/provider-sync`
        })
        this.providerSyncApplied = true
        await this.finishProviderSync()
      } catch (err) {
        this.syncError = this.normalizeError(err, 'The provider data could not be synced.')
      } finally {
        this.isOrderUpdating = false
      }
    },

    async finishProviderSync () {
      const updatedOrder = await this.$store.dispatch('sh/dataApi', {
        method: 'get',
        uri: `order/${this.order.id}`,
        opts: {
          params: { with: 'order_details,shipper,receiver' }
        }
      })

      this.$emit('updated', updatedOrder)
      this.compareResult = Object.assign({}, this.compareResult, { diff: { same: true } })
      this.syncSucceeded = true
      this.syncError = null
    },

    async retrySync () {
      if (this.isOrderUpdating) return
      if (!this.providerSyncApplied) {
        await this.syncProviderData()
        return
      }

      this.isOrderUpdating = true
      this.syncError = null
      try {
        await this.finishProviderSync()
      } catch (err) {
        this.syncError = this.normalizeError(err, 'The synced order data could not be loaded.')
      } finally {
        this.isOrderUpdating = false
      }
    }
  }
}
</script>

<style scoped>
  .delivery-order-provider-sync {
    width: 100%;
  }

  .sync-checking {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 48px;
    color: #90A4AE;
  }

  .sync-guard {
    border: 1px solid #FF9F00;
    border-radius: 16px;
    background-color: #252525;
  }

  .sync-guard-content {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 18px;
    max-width: 470px;
    margin: 0 auto;
  }

  .sync-guard-icon {
    display: none;
    font-size: 38px !important;
  }

  .sync-guard-title {
    color: #FF9F00;
    font-size: 1.28rem;
    font-weight: 600;
    text-align: center;
  }

  .sync-guard-action {
    min-width: 250px;
  }

  .sync-guard-message {
    margin-top: 24px;
    color: #ffffff;
    font-size: 1.05rem;
    font-weight: 500;
    text-align: center;
  }

  .sync-dialog {
    display: flex;
    flex-direction: column;
    height: min(820px, 90vh);
    overflow: hidden;
    background-color: #1e1e1e !important;
  }

  .sync-dialog--fullscreen {
    height: 100vh;
    max-height: 100vh;
  }

  .sync-dialog-header {
    display: block;
    flex: 0 0 auto;
    min-height: 118px;
    padding: 20px 30px !important;
    border-bottom: 1px solid #333333;
  }

  .sync-dialog-heading-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  .sync-dialog-title {
    min-width: 0;
    color: #bdbdbd;
    font-size: 1.5rem;
    font-weight: 400;
    line-height: 1.3;
  }

  .sync-dialog-subtitle {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
    color: #90A4AE;
    font-size: 1.05rem;
    line-height: 1.3;
  }

  .sync-dialog-status-separator {
    margin: 0 12px;
    color: #727272;
  }

  .sync-dialog-status-label {
    color: #727272;
    font-style: italic;
  }

  .sync-details-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 0 !important;
  }

  .sync-dialog-actions {
    flex: 0 0 72px;
    min-height: 72px;
    padding: 12px 28px !important;
    border-top: 1px solid #333333;
    gap: 20px;
  }

  .sync-dialog-warning {
    flex: 1 1 auto;
    color: #ffffff;
    font-weight: 500;
    text-align: center;
  }

  .sync-result-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 70px 32px 30px !important;
  }

  .sync-success-heading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 28px;
    color: #FF9F00;
    font-size: 1.3rem;
    font-weight: 600;
  }

  .sync-success-icon {
    font-size: 72px !important;
    animation: sync-check-appear 420ms cubic-bezier(0.2, 0.9, 0.3, 1.3) both;
  }

  .sync-reminder {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 28px;
    margin-top: 64px;
    font-size: 1.18rem;
    font-weight: 600;
    line-height: 1.25;
  }

  .sync-reminder-icon {
    font-size: 36px !important;
  }

  .sync-error-icon {
    display: block;
    margin: 0 auto;
    font-size: 64px !important;
  }

  .sync-error-heading {
    margin-top: 24px;
    color: #EF5350;
    font-size: 1.3rem;
    font-weight: 600;
    text-align: center;
  }

  .sync-error-message {
    max-width: 560px;
    margin: 18px auto 0;
    text-align: center;
  }

  .sync-error-actions {
    gap: 18px;
  }

  @keyframes sync-check-appear {
    from {
      opacity: 0;
      transform: scale(0.45) rotate(-12deg);
    }
    to {
      opacity: 1;
      transform: scale(1) rotate(0);
    }
  }

  @media screen and (min-width: 600px) {
    .sync-guard-content {
      grid-template-columns: auto auto;
      justify-content: center;
      column-gap: 10px;
    }

    .sync-guard-icon {
      display: inline-flex;
    }

    .sync-guard-title {
      text-align: left;
    }

    .sync-guard-action {
      grid-column: 1 / -1;
      justify-self: center;
    }
  }

  @media screen and (max-width: 600px) {
    .sync-guard-content {
      grid-template-columns: 1fr;
      justify-items: center;
    }

    .sync-dialog-actions {
      flex-basis: 112px;
      flex-wrap: wrap;
      justify-content: center;
    }

    .sync-dialog-warning {
      flex-basis: 100%;
      order: -1;
    }

    .sync-dialog-header {
      min-height: 132px;
      padding: 16px 18px !important;
    }

    .sync-dialog-title {
      font-size: 1.25rem;
    }

    .sync-dialog-status-separator {
      margin: 0 4px;
    }
  }
</style>
